import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  doc,
  updateDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { addOptimisticPost, removeOptimisticPost } from "../../../redux/slices/postSlice";
import { getFirebaseErrorMessage } from "../../../utils/firebaseErrors";
import { savePostToFirebase } from "../../../pages/auth/utils/firebase/userOperations";

// Debounce map: postId -> last increment timestamp
const _shareCountDebounce = new Map();
const DEBOUNCE_MS = 2000;

// Increment share count on a specific post (debounced to prevent spam)
export const incrementShareCount = createAsyncThunk(
  "share/incrementShareCount",
  async (postId, { rejectWithValue }) => {
    try {
      // Debounce: ignore calls for the same postId within DEBOUNCE_MS
      const now = Date.now();
      const lastTime = _shareCountDebounce.get(postId) || 0;
      if (now - lastTime < DEBOUNCE_MS) {
        return postId; // Silently skip
      }
      _shareCountDebounce.set(postId, now);

      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, {
        shares: increment(1),
        updatedAt: serverTimestamp(),
      });
      return postId;
    } catch (error) {
      return rejectWithValue(getFirebaseErrorMessage(error));
    }
  },
);

// Reshare a post by creating a new document with type: shared_post
export const resharePost = createAsyncThunk(
  "share/resharePost",
  async (payload, { dispatch, rejectWithValue }) => {
    const tempId = `temp-reshare-${Date.now()}`;
    try {
      const {
        originalPostId,
        commentary,
        originalPostData,
        userId,
        username,
        userPhotoURL,
        userProfession,
      } = payload;

      // Optimistically add the reshare to the feed immediately
      dispatch(addOptimisticPost({
        id: tempId,
        userId,
        username,
        userPhotoURL,
        userProfession: userProfession || "",
        content: commentary || "",
        title: `Shared: ${originalPostData.title || "Post"}`,
        actionTag: "share",
        issue: originalPostData.issue || null,
        issueStatus: originalPostData.issueStatus || null,
        scale: originalPostData.scale || "local",
        location: originalPostData.location || "",
        type: "shared_post",
        sharedPostId: originalPostId,
        sharedPostData: originalPostData,
        timestamp: new Date(),
        status: "active",
        likeCount: 0,
        commentCount: 0,
        shares: 0,
      }));

      const reshareData = {
        userId,
        username,
        userPhotoURL,
        userProfession: userProfession || "",
        content: commentary || "",
        title: `Shared: ${originalPostData.title || "Post"}`,
        actionTag: "share",
        issue: originalPostData.issue || null,
        issueStatus: originalPostData.issueStatus || null,
        scale: originalPostData.scale || "local",
        location: originalPostData.location || "",
        type: "shared_post",
        sharedPostId: originalPostId,
        sharedPostData: originalPostData,
        timestamp: serverTimestamp(),
        status: "active",
      };

      // 1. Create the new post document
      const newPost = await savePostToFirebase(reshareData);

      // 2. Increment share count — only reached when savePostToFirebase succeeded above
      dispatch(incrementShareCount(originalPostId));

      // Remove optimistic post (the listener will add the real one)
      dispatch(removeOptimisticPost(tempId));

      return newPost;
    } catch (error) {
      // Remove optimistic post on failure
      dispatch(removeOptimisticPost(tempId));
      return rejectWithValue(getFirebaseErrorMessage(error));
    }
  },
);
